import { IoCloudUploadOutline } from "react-icons/io5";
import { FaTimes } from "react-icons/fa";
import React, { useRef, useState, useCallback, useEffect } from "react";
import { FcApproval, FcCancel } from "react-icons/fc";
import { getPresignedUrl } from '@/service/s3';

// Simple Toast Context and Provider
const ToastContext = React.createContext<{
  showToast: (msg: string, type?: "success" | "error") => void;
} | null>(null);

const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toast, setToast] = useState<{
    msg: string;
    type: "success" | "error";
  } | null>(null);
  const [visible, setVisible] = useState(false);

  const showToast = useCallback(
    (msg: string, type: "success" | "error" = "success") => {
      setToast({ msg, type });
      setVisible(true);
    },
    []
  );

  useEffect(() => {
    if (visible) {
      const timer = setTimeout(() => setVisible(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [visible]);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {visible && toast && (
        <div
          className="fixed top-6 left-1/2 -translate-x-1/2 px-4 py-2 shadow-card rounded-lg z-50
             transition-all bg-white flex items-center gap-2"
        >
          {toast.type === "success" ? <FcApproval /> : <FcCancel />}
          {toast.msg}
        </div>
      )}
    </ToastContext.Provider>
  );
};

function useToast() {
  const ctx = React.useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx.showToast;
}

interface FileUploaderProps {
  setFiles: (values: string[]) => void;
  files: string[];
}

const FileUploader: React.FC<FileUploaderProps> = ({ setFiles, files }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const showToast = useToast();

  const uploadFile = async (file: File) => {
    // 1. Get presigned URL
    const key = `priceworth/${Date.now()}-${file.name}`;
    const contentType = file.type;
    let url = '';
    try {
      const response = await getPresignedUrl({ key, contentType });
      url = response.data.url;
    } catch (err) {
      showToast('Failed to get upload URL', 'error');
      return Promise.reject(err);
    }

    // 2. Upload file to S3
    try {
      const uploadRes = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': contentType },
        body: file,
      });
      if (!uploadRes.ok) throw new Error('Upload to S3 failed');
      // For public S3 bucket, the file URL is the presigned URL without query params
      const fileUrl = url.split('?')[0];
      showToast(`${file.name} uploaded successfully`, 'success');
      return fileUrl;
    } catch (err) {
      showToast(`${file.name} upload failed`, 'error');
      return Promise.reject(err);
    }
  };

  const handleFiles = async (fileList: FileList) => {
    const uploadPromises = Array.from(fileList).map(uploadFile);
    try {
      const locations = await Promise.all(uploadPromises);
      setFiles([...files, ...locations]);
    } catch (err) {
      // Error is already handled in uploadFile with toast
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(e.target.files);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleRemove = (url: string) => {
    setFiles(files.filter((item) => item !== url));
  };

  return (
    <div
      className="border-dashed border-2 p-6 rounded-lg text-center cursor-pointer bg-white"
      onClick={() => inputRef.current?.click()}
      onDrop={handleDrop}
      onDragOver={(e) => e.preventDefault()}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleInputChange}
      />
      <div className="mb-2 flex flex-col items-center gap-2">
        <IoCloudUploadOutline className="text-7xl" />
        <p className="text">Browser or drag images here</p>
      </div>
      <div className="flex flex-wrap gap-2 justify-center mt-4">
        {files.map((url) => (
          <div key={url} className="relative w-24 h-24">
            <img
              src={url}
              alt=""
              className="object-cover w-full h-full rounded"
            />
            <FaTimes
              className="absolute top-0.5 right-0.5 text-red-500 text-xl"
              onClick={(e) => {
                e.stopPropagation();
                handleRemove(url);
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

// Export with ToastProvider wrapper
const FileUploaderWithToast: React.FC<FileUploaderProps> = (props) => (
  <ToastProvider>
    <FileUploader {...props} />
  </ToastProvider>
);

export default FileUploaderWithToast;
